import { Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import type { FilterChipModel } from '@/utils/filterChips'
import { useFilterChipLabels } from '@/hooks/useFilterChips'

export function FilterChip({ chip, marker, tooltip = false }: { chip: FilterChipModel; marker: boolean; tooltip?: boolean }) {
  const pill = (
    <span
      className={`filter-chip filter-chip--${chip.kind}`}
      style={{ backgroundColor: chip.color, color: chip.ink, borderColor: chip.ink }}
    >
      <span className="filter-chip-label">{marker ? chip.glyph : chip.label}</span>
    </span>
  )
  return tooltip ? <Tooltip title={chip.title}>{pill}</Tooltip> : pill
}

interface FilterChipsProps {
  chips: FilterChipModel[] | undefined
  /**
   * `auto` condenses to markers past two chips, `full` always shows labels, `markers` always shows markers.
   * The "show labels" preference turns `auto` and `markers` into `full`.
   */
  variant?: 'auto' | 'full' | 'markers'
  /** Markers name themselves in a tooltip. Full chips never get one. */
  tooltips?: boolean
  /** Beside text that already names the chip: no screen-reader text, and the labels preference is ignored. */
  decorative?: boolean
  /**
   * Id for the screen-reader phrase, for an `aria-describedby` on a nearby link. The phrase is then
   * left out of the reading order so it is announced once, as the link's description.
   */
  descriptionId?: string
  className?: string
}

export function FilterChips({
  chips,
  variant = 'auto',
  tooltips = false,
  decorative = false,
  descriptionId,
  className,
}: FilterChipsProps) {
  const { t } = useTranslation()
  const labels = useFilterChipLabels() && !decorative
  if (!chips || chips.length === 0) return null
  const mode = labels ? 'full' : variant === 'auto' ? (chips.length > 2 ? 'markers' : 'full') : variant

  const names = (kind: FilterChipModel['kind']) => chips.filter((c) => c.kind === kind).map((c) => c.title).join(', ')
  const frameworks = names('framework')
  const contexts = names('context')

  return (
    <>
      <span
        className={`filter-chips filter-chips--${mode}${className ? ` ${className}` : ''}`}
        aria-hidden="true"
      >
        {chips.map((chip) => (
          <FilterChip key={chip.key} chip={chip} marker={mode === 'markers'} tooltip={tooltips && mode === 'markers'} />
        ))}
      </span>
      {!decorative && (
        <span className="sr-only" id={descriptionId} hidden={descriptionId !== undefined}>
          {frameworks && ` ${t('filterChips.relatedFrameworks', { names: frameworks })}`}
          {contexts && ` ${t('filterChips.relatedContexts', { names: contexts })}`}
        </span>
      )}
    </>
  )
}
